import { Link } from 'react-router-dom';
import { useLocalizedPath } from '@/hooks/useI18n';

/* ═══════════════════════════════════════════
   PRAIA WIDGETS — apresentação visual dos dados
   de praia (mesmo vocabulário visual do Guia).
   Derivam de campos comuns a todas as praias.
   ═══════════════════════════════════════════ */

interface Entity {
  title: string;
  vilarejoName?: string;
  waveHeight?: string;
  difficulty?: string;
  crowdLevel?: string;
  access?: string;
  bestTide?: string;
  surfFriendly?: boolean;
  snorkelFriendly?: boolean;
  kidsFriendly?: boolean;
  averageTemp?: { summer?: number; winter?: number };
  oQueLevar?: string[];
}

/* ─── Ficha rápida — stats escaneáveis ─── */
export function PraiaQuickFacts({ entity: e }: { entity: Entity }) {
  const facts = [
    e.waveHeight && { icon: '🌊', label: 'Mar', value: e.waveHeight },
    e.difficulty && { icon: '🥾', label: 'Acesso', value: e.difficulty },
    e.crowdLevel && { icon: '👥', label: 'Movimento', value: e.crowdLevel },
    e.averageTemp?.summer && {
      icon: '🌡️',
      label: 'Água',
      value: `${e.averageTemp.winter ?? ''}–${e.averageTemp.summer}°C`,
    },
  ].filter(Boolean) as { icon: string; label: string; value: string }[];

  const tags = [
    e.surfFriendly && { icon: '🏄', label: 'Surf' },
    e.snorkelFriendly && { icon: '🤿', label: 'Snorkel' },
    e.kidsFriendly && { icon: '👨‍👩‍👧', label: 'Família' },
    !e.kidsFriendly && !e.surfFriendly && !e.snorkelFriendly && { icon: '🌿', label: 'Sossego' },
  ].filter(Boolean) as { icon: string; label: string }[];

  if (!facts.length) return null;

  return (
    <div className="bg-white rounded-2xl border border-sand-200 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {facts.map(f => (
          <div key={f.label} className="bg-sand-50 rounded-xl p-3 text-center">
            <div className="text-2xl mb-1" role="img" aria-label={f.label}>
              {f.icon}
            </div>
            <div className="font-display text-base text-warm-dark leading-tight">{f.value}</div>
            <div className="font-body text-xs text-warm-gray mt-0.5">{f.label}</div>
          </div>
        ))}
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          <span className="font-body text-xs font-semibold text-warm-gray uppercase tracking-wider self-center">
            Ideal para
          </span>
          {tags.map(t => (
            <span
              key={t.label}
              className="inline-flex items-center gap-1.5 bg-teal-light/10 text-teal-deep border border-teal-light/20 font-body text-sm font-semibold px-3 py-1 rounded-full"
            >
              <span role="img" aria-hidden="true">
                {t.icon}
              </span>
              {t.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Dualidade da maré — surf x piscinas ─── */
export function PraiaTideSplit({ entity: e }: { entity: Entity }) {
  // Só faz sentido quando a praia tem as duas faces (surf na alta, snorkel na baixa)
  if (!e.surfFriendly || !e.snorkelFriendly) return null;

  return (
    <div className="bg-white rounded-2xl border border-sand-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <h2 className="font-display text-xl text-warm-dark mb-1 tracking-wide">
        Surf ou piscinas? Depende da maré
      </h2>
      <p className="font-body text-sm text-warm-gray mb-5">
        A {e.title} muda de personalidade com o vai e vem da maré.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border-2 border-teal-200 bg-teal-50 p-5">
          <div className="text-3xl mb-2" role="img" aria-label="Maré alta">
            🏄
          </div>
          <h3 className="font-display text-lg text-warm-dark mb-1">Maré alta · Surf</h3>
          <p className="font-body text-sm text-warm-gray">
            Ondas {(e.waveHeight || '').toLowerCase()} atraem surfistas. Atenção à correnteza para
            quem não tem experiência.
          </p>
        </div>
        <div className="rounded-xl border-2 border-sand-200 bg-sand-50 p-5">
          <div className="text-3xl mb-2" role="img" aria-label="Maré baixa">
            🤿
          </div>
          <h3 className="font-display text-lg text-warm-dark mb-1">Maré baixa · Piscinas</h3>
          <p className="font-body text-sm text-warm-gray">
            A água recua entre as rochas e forma piscinas naturais de água cristalina, ideais para
            snorkel e crianças.
          </p>
        </div>
      </div>
      {e.bestTide && (
        <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <span role="img" aria-label="Dica" className="text-lg flex-shrink-0">
            💡
          </span>
          <p className="font-body text-sm text-warm-gray">
            <span className="font-semibold text-warm-dark">Melhor maré:</span> {e.bestTide}.
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Como chegar — timeline visual ─── */
export function PraiaArrivalTimeline({ entity: e }: { entity: Entity }) {
  if (!e.vilarejoName || !e.access) return null;

  const steps = [
    { icon: '⛵', title: `Trapiche de ${e.vilarejoName}`, desc: 'Desembarque na ilha' },
    { icon: '🥾', title: e.access, desc: 'A ilha é livre de carros — tudo a pé' },
    { icon: '🏖️', title: e.title, desc: 'Você chegou!' },
  ];

  return (
    <div className="grid sm:grid-cols-3 gap-3">
      {steps.map((s, i) => (
        <div key={i} className="relative">
          <div className="bg-sand-50 rounded-xl border border-sand-200 p-4 h-full">
            <div className="flex items-center gap-2 mb-1">
              <span
                className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-deep text-white flex items-center justify-center font-display text-sm"
                aria-hidden="true"
              >
                {i + 1}
              </span>
              <span className="text-xl" role="img" aria-hidden="true">
                {s.icon}
              </span>
            </div>
            <div className="font-display text-base text-warm-dark leading-tight">{s.title}</div>
            <div className="font-body text-xs text-warm-gray mt-1">{s.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── O que levar — grid de cards com check ─── */
export function PraiaPackingGrid({ items }: { items?: string[] }) {
  if (!items?.length) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {items.map((item, i) => (
        <div
          key={i}
          className="flex items-start gap-3 bg-white rounded-xl border border-sand-200 p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
        >
          <svg
            className="w-5 h-5 text-teal-deep mt-0.5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="font-body text-sm text-warm-gray">{item}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Tabela comparativa com as praias vizinhas ─── */
function idealPara(p: Entity): string {
  const tags = [
    p.surfFriendly && 'Surf',
    p.snorkelFriendly && 'Snorkel',
    p.kidsFriendly && 'Família',
    !p.surfFriendly && !p.snorkelFriendly && !p.kidsFriendly && 'Sossego',
  ].filter(Boolean);
  return tags.join(' · ');
}

export function PraiaComparativa({
  entity,
  relacionadas,
}: {
  entity: Entity & { handle?: string };
  relacionadas: (Entity & { handle: string })[];
}) {
  const localize = useLocalizedPath();
  if (!relacionadas?.length) return null;
  const linhas: ((Entity & { handle?: string; atual?: boolean })[]) = [
    { ...entity, atual: true },
    ...relacionadas,
  ];

  return (
    <div className="bg-white rounded-2xl border border-sand-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <h2 className="font-display text-xl text-warm-dark mb-1 tracking-wide">
        Como esta praia se compara às vizinhas
      </h2>
      <p className="font-body text-sm text-warm-gray mb-4">
        Compare e escolha a praia certa para o seu dia na ilha.
      </p>
      <div className="overflow-x-auto rounded-xl border border-sand-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-teal-deep text-white">
              <th className="text-left px-4 py-2.5 font-body font-semibold whitespace-nowrap">Praia</th>
              <th className="text-left px-4 py-2.5 font-body font-semibold whitespace-nowrap">Mar</th>
              <th className="text-left px-4 py-2.5 font-body font-semibold whitespace-nowrap">Ideal para</th>
              <th className="text-left px-4 py-2.5 font-body font-semibold whitespace-nowrap">Acesso</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sand-100">
            {linhas.map((p, i) => (
              <tr key={p.handle || 'atual'} className={p.atual ? 'bg-teal-light/10' : i % 2 === 0 ? 'bg-white' : 'bg-sand-50'}>
                <td className="px-4 py-2.5 font-body font-medium whitespace-nowrap">
                  {p.atual ? (
                    <span className="text-warm-dark">{p.title} <span className="text-warm-gray font-normal">(você está aqui)</span></span>
                  ) : (
                    <Link to={localize(`/praias/${p.handle}`)} className="text-teal-deep hover:underline">
                      {p.title}
                    </Link>
                  )}
                </td>
                <td className="px-4 py-2.5 font-body text-warm-gray">{p.waveHeight || '—'}</td>
                <td className="px-4 py-2.5 font-body text-warm-gray">{idealPara(p) || '—'}</td>
                <td className="px-4 py-2.5 font-body text-warm-gray">{p.difficulty || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── CTA tábua de maré — link interno clicável ─── */
export function PraiaTideCTA() {
  const localize = useLocalizedPath();
  return (
    <Link
      to={localize('/tabela-de-mare-2026')}
      className="group flex items-center gap-4 bg-teal-deep text-white rounded-2xl p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
    >
      <span className="text-3xl flex-shrink-0" role="img" aria-hidden="true">
        🌊
      </span>
      <div className="flex-1">
        <div className="font-display text-lg leading-tight">Confira a tábua de maré 2026</div>
        <div className="font-body text-sm text-white/80">
          Acerte o horário da maré para aproveitar melhor a praia — acesso, banho e piscinas
          naturais.
        </div>
      </div>
      <svg
        className="w-5 h-5 flex-shrink-0 transition-transform group-hover:translate-x-1"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}
